import type { Lang } from "./copy";
import type { ClassSort, Fraction, SchoolNote } from "./schoolDashboardModel";

/**
 * E1, E5, E6 and S1's copy.
 *
 * ⚠️ A SEPARATE FILE FROM `copy.ts`, WHICH IS S3's AND BELONGS TO ANOTHER SLICE.
 * The alternative — editing a file another slice owns — is the collision the
 * unit contract exists to prevent. `Lang` is imported rather than redeclared, so
 * there is still one definition of it.
 *
 * ⚠️ THIS TABLE HAD A MALAYALAM COLUMN (ADR-044), and `adminCopy.test.ts`
 * carried the gate that went with it: no Malayalam string identical to its
 * English original, with an allowlist that had to justify itself entry by
 * entry. Both are gone — a half-translated screen is not a state this product
 * can reach any more.
 */

export const ADMIN_COPY = {
  en: {
    /*
     * ── the plan screen, before there is a plan ──
     *
     * 🔴 IT DESCRIBES THE PROCESS, NOT THE HOLE. The screen used to render
     * `superadmin/billingCopy`'s "No subscription" — an operator's status word
     * for one cell of a table of hundreds of schools, shown to the school
     * itself with nothing beside it.
     *
     * ⚠️ NOTHING HERE PROMISES SELF-SERVICE. Every subscription write is
     * operator-only (*"a school changing its own plan is a discount waiting to
     * happen"*), so the words must not imply a button exists somewhere on this
     * page. They name the price list and the conversation, which are the two
     * real things.
     */
    planNoneTitle: "No plan on record yet.",
    planNoneWhy:
      "Kalqy sets a school’s plan up during onboarding — nothing is charged until it is in place, and the product keeps working while it is arranged.",
    planNoneChildren: "Children enrolled",
    planNoneChildrenWhy: "What a quote would be counted against today",
    planNoneListTitle: "Published plans",
    planNoneListWhy:
      "The same price list as the public site. Prices are per child, per billing period.",
    planNoneListEmpty: "No plans have been published yet.",
    planNoneListFailed: "The price list could not be loaded just now.",
    planNonePerChild: (period: string) => `per child · ${period}`,
    planNoneFloor: (amount: string) =>
      `Minimum charge ${amount} — a small school pays this however few children it has.`,
    planNoneAsk:
      "To start a plan, speak to Kalqy — they put the plan on this school and it appears here.",

    /* ── E1 ── */
    dashboardTitle: "School dashboard",
    children: "Children",
    classes: "Classes",
    teachers: "Teachers",
    sessionsThisWeek: "Sessions this week",
    academicYear: "Academic year",
    academicYearUnset: "Not set yet",

    /**
     * 🔴 THE SENTENCE OD-39 AND OD-72 EXIST FOR. A denominator, always, and
     * never a percentage: a school reads "30%" as a grade, and its meaning
     * depends entirely on what it was divided by.
     */
    playedThisWeek: (f: Fraction) =>
      `${f.count} of ${f.total} children played this week`,
    touchedSkills: (f: Fraction) =>
      `${f.count} of ${f.total} children have any recorded activity`,

    /*
     * ⚠️ THE WINDOW TRAVELS WITH EVERY FIGURE IT BOUNDS. The server sends
     * `windowDays` beside the counts precisely so a screen cannot render "23
     * played" without saying over what — and the tile said "this week" while the
     * server was free to answer for any window it liked.
     */
    overWindow: (days: number) => `Last ${String(days)} days`,

    /* ── E1's date filter, which now bounds every play figure on the page ── */
    /*
     * 🔴 THE HEADING IS A CLAIM ABOUT SCOPE, AND IT IS DELIBERATELY NARROW.
     * "Filtering the dashboard" would be a lie: the filter moves the play
     * figures and leaves the head counts alone, because a school does not have
     * fewer children because you asked about February. Saying which is which
     * here is cheaper than a reader discovering it by distrusting a tile.
     */
    rangeLegend: "Dates",
    rangeFrom: "From",
    rangeTo: "To",
    /* ⚠️ A DATE FORMATTED FOR READING, NOT THE ISO STRING THE INPUTS HOLD.
       "5 Mar 2026" is what a person checks a range against; "2026-03-05" is
       what the API speaks, and the two have no business being the same. */
    rangeShowing: (from: string, to: string, days: number) =>
      from === to
        ? `Showing ${from} — one day`
        : `Showing ${from} – ${to} — ${String(days)} days`,
    /*
     * 🔴 THE SENTENCE THAT STOPS THE HEAD-COUNT TILES READING AS A BUG. Without
     * it, an admin who narrows to one week and watches "Children 33" refuse to
     * move concludes the filter is broken, when it is being correct.
     */
    rangeScopeNote:
      "Children, teachers, classes and consent are counts as of today — only the play figures follow these dates.",
    /*
     * ⚠️ DRAWN ONLY WHEN THE SERVER CAME BACK WITH A DIFFERENT RANGE FROM THE
     * ONE SENT. It resolves rather than refuses — a backwards pair swaps, a
     * future end clamps to today, a span over a year moves its start — and a
     * page that silently showed the adjusted numbers under the requested dates
     * would be the dishonest half of that bargain.
     */
    rangeAdjusted: "Adjusted to the range above — the dates asked for could not be used as given.",
    /* 🔴 SAYS WHAT IS WRONG, NOT WHAT IS MISSING: they are invisible to every
       class list, which is why an admin should care. */
    childrenNotInAClass: (n: number) =>
      n === 1 ? `1 child is not in a class` : `${String(n)} children are not in a class`,
    archivedClasses: (n: number) =>
      n === 1 ? `1 archived` : `${String(n)} archived`,

    /* ── E1's trend, and the window control over it ── */
    trendTitle: "Is the product being used?",
    trendHint: (days: number) =>
      `Sittings, and the children behind them, on each of the last ${String(days)} days.`,
    trendWindow: "Window",
    trendDays: (days: number) => `${String(days)} days`,
    trendSessions: "Sittings",
    trendChildren: "Children who played",
    trendNothing: "No sittings in this window yet.",
    /*
     * ⚠️ THE BASIS LINE COMES FROM THE SERVER, NOT FROM HERE. `SchoolActivity`
     * carries `basis` in words precisely so a chart cannot mislabel what it is
     * counting; a second sentence in this file would be a second answer.
     */

    /*
     * ── E1's consent tile: REMOVED, along with the four strings it needed ──
     *
     * The strip no longer carries a whole-school consent count, so `tileConsent`,
     * its two captions and `consentConsequence` — the sentence that had to
     * travel with the number, because *"a bare number would lose the sentence"* —
     * have gone with it. E1's consent copy is now `consentHint` and
     * `openConsent` further down, which point at the per-class screen.
     */
    consentOpen: "Consent, class by class",

    /* ── The ranked class list ── */
    classesRankedMore: (hidden: number) =>
      hidden === 1 ? "View 1 more class" : `View all ${String(hidden)} more classes`,
    classesRankedFewer: "Show fewer",

    /*
     * ── Quick actions: REMOVED, and the four strings with it ──
     *
     * The bar is gone from E1, so `quickActions`, `quickAddClass`,
     * `quickAddTeacher` and `quickAddChild` have nothing to label. It had
     * already lost half its buttons before this: "Assign Game" and "Create
     * Assignment" named features that do not exist anywhere in the schema, and
     * "Send a notice" went with the broadcast screen it opened. The three that
     * survived were shortcuts to three sidebar items — see the screen for why a
     * shortcut can be deleted where an entrance cannot.
     */

    whoHasPlayed: "Who has played",
    whoHasPlayedHint: (days: number) =>
      `Children who have played in the last ${String(days)} days, and children who have ever played.`,
    busiestClasses: "Busiest classes",
    busiestClassesHint: (days: number) =>
      `The largest share of a class playing in the last ${String(days)} days.`,
    /* A school where nobody has played is a real state and a common first week,
       so it gets a sentence rather than an empty panel. */
    noClassPlayedYet: "No class has played yet.",

    noteNoChildren: "No children yet. Import a roster to get started.",
    noteNobodyPlayed: "Nobody has played this week.",
    /** ⚠️ Says what is actually wrong: they are invisible, not merely unassigned. */
    noteChildrenWithNoClass: (n: number) =>
      `${n} children are not in any class, so they do not appear in any class list. Move them into a class.`,
    noteNoTeachers: "No active teachers. Nobody can run a session yet.",

    className: "Class",
    band: "Band",
    teacher: "Teacher",
    played: "Played this week",
    archived: "Archived",
    year: "Year",
    openConsent: "Consent",
    /** ⚠️ E1 links to S3 rather than restating consent — see the screen. */
    consentHint: "Consent is tracked per class on its own screen.",
    sortLabel: "Order",
    sortName: "By name",
    sortQuietest: "Quietest first",
    sortLargest: "Largest first",

    /* ── E5 ── */
    reportsTitle: "School reports",
    coverageTitle: "NCF-FS coverage across the school",
    /** 🔴 A stated denominator, and the word "so far", because zero is a state. */
    coverageCount: (f: Fraction) =>
      `${f.count} of ${f.total} competency codes have activity behind them`,
    coverageRule:
      "A code counts as covered once one child has played a game that carries it.",
    /** ⚠️ OD-59 — this is not a Holistic Progress Card, and it is not a PDF. */
    coverageNotACard:
      "This is a coverage summary, not a Holistic Progress Card, and there is no PDF. Export the spreadsheet to work with it.",
    physicalCovered:
      "Physical development (C-3) is covered by the movement games.",
    physicalUncovered:
      "Physical development (C-3) has no activity behind it yet.",
    untouchedGames: "Games nobody has played yet",
    game: "Game",
    category: "Category",
    codes: "NCF-FS",
    events: "Activity",
    exportCsv: "Export spreadsheet",
    covered: "Covered",
    uncovered: "No activity yet",

    /*
     * ── E1's games section ──
     *
     * 🔴 IT ANSWERS "WHICH GAMES IS THIS SCHOOL ACTUALLY USING", which nothing
     * else in the tier can. The class table counts children who played
     * *something*; a school where everybody plays Sky Quest and nobody has
     * opened Kalqy World looks identical in it. `games.ts` already records
     * that the documented default order omits Kalqy World, so "a child
     * playing only default sessions never meets Social Awareness" — this is the
     * first screen where a school could notice.
     */
    gamesTitle: "The five games",
    gamesHint:
      "One game teaches each skill. Counts are children who have played it at least once.",
    /*
     * 🔴 THE ONE SECTION THE DATE FILTER DOES NOT REACH, SAYING SO IN ITS OWN
     * HINT. `SkillsService.breakdownFor` counts all events ever and has no time
     * window at all, and windowing it would be worse than leaving it: "highest
     * level reached" inside a range shows Level 2 for a child who finished
     * Level 5 last term, which is a report card that under-sells a child.
     * `everPlayed` already sets the precedent for labelling an all-time figure
     * as one instead of quietly mixing it in with windowed ones.
     */
    gamesAllTime: "All time — not the selected dates.",
    gameChildrenPlayed: "children have played it",
    /** ⚠️ Not an error state. A game nobody has opened is a fact, not a fault. */
    /* ⚠️ A VERB, BECAUSE THE CARD IS A DOOR NOW. "See the log" says what the
       click does; a card that only changed colour would leave a reader
       guessing whether it filters, drills in, or opens the game itself. */
    gameOpenLog: "See who played",
    gameUntouched: "Nobody has played this yet",

    /** E1's hero chips — both carry their denominator inside the chip. */
    playedWindow: (days: number) => `played in the last ${String(days)} days`,
    everPlayedLabel: "have ever played",

    /*
     * ── E5's game cards ──
     *
     * 🔴 BOTH COUNTS KEEP A LABEL, because they are facts about different
     * things. A game played hard by four children and one touched once by
     * twenty-three can carry the same total, and only the pair distinguishes
     * them.
     */
    coverageActivity: "activities",
    coverageChildren: "children",
    /** ⚠️ A fact, not a failure — `summariseCoverage` calls these "where the gap
        actually is", and it is the one thing on E5 a school can act on. */
    coverageUntouched: "Not played yet",
    coverageCovered: "Codes with activity behind them",
    coverageMissing: "Codes with nothing yet",
    coverageGames: "By game",

    save: "Save",
    saved: "Saved",

    /** ⚠️ Both ends inclusive — an exclusive end is an off-by-one nobody notices
        until the school opens on a festival. */

    /* ── S1 ── */
    rolloverTitle: "Roll over the academic year",
    rolloverIntro:
      "Move each class up a band into next year’s class, and move leavers into a leavers class that is then archived. Children keep their history, their roll number and their parent.",
    fromYear: "Year ending",
    toYear: "Year starting",
    moveInto: "Move into",
    leaveAlone: "Leave this class alone",
    /** 🔴 D4 — a top-band class is offered one action, and it is the true one. */
    actionPromote: "Move up a band",
    actionGraduate: "Leaving school",
    topBandHint: "This is the top band, so these children are leaving.",
    leaversNameHint: (name: string) =>
      `Create a class called "${name}" first, then choose it here.`,
    preview: "Check this",
    run: "Run the rollover",
    resume: "Finish the rollover",
    verdictEmpty: "Nothing to move yet. Choose where each class goes.",
    verdictReady: "Ready. Nothing has been changed yet.",
    /** 🔴 All or nothing: one problem blocks the whole cohort, and it says so. */
    verdictBlocked:
      "This cannot run yet. Every problem below has to be fixed first: a rollover moves the whole school or none of it.",
    verdictAlreadyRan:
      "This year has already been rolled over. Running it again would change nothing.",
    movesInto: (n: number, className: string) =>
      `${n} children move into ${className}`,
    bandChanges: (n: number) => `${n} of them move up a band`,
    bandKept:
      "Children whose teacher moved them to a different band stay the same distance from their class.",
    archivedAfter: "archived once its children have arrived",
    rolloverHistory: "Rollovers so far",
    ranOn: (at: string) => `Completed ${at}`,
    neverCompleted: "Started but never finished",
    promoted: "Moved up",
    graduated: "Left school",

    /* ── E3 · teachers ── */
    teachersTitle: "Teachers",
    teachersSubtitle:
      "Everyone who teaches at this school, and which class each of them runs.",
    /* ⚠️ `teacher` and `className` are E1's, reused above rather than redeclared
       here — the same column on two screens must not be able to drift into two
       different words. */
    email: "Email",
    role: "Role",
    status: "Status",
    classesColumn: "Classes",
    noClassesYet: "No class yet",
    staffEmpty: "No teachers yet. Add the first one below.",

    /* The four states, and they are four because two of them look identical in
       the database and need opposite actions. */
    stateActive: "Can sign in",
    /* ⚠️ Nothing produces these two any more — the school sets the password, so
       an account works from the moment it is created. They describe rows left
       over from the invitation flow, and both point at the same fix. */
    statePending: "Waiting for an invitation link — set a password instead",
    stateExpired: "Invitation expired — set a password instead",
    stateDeactivated: "Cannot sign in",

    addTeacher: "Add a teacher",
    addTeacherHint:
      "The account works straight away. Give them the password you type here.",
    teacherName: "Full name",
    teacherEmail: "Work email",
    teacherPassword: "Password",
    /* 🔴 IT SAYS THE ADMIN WILL KNOW IT, rather than leaving them to assume
       otherwise. That is the honest cost of the school issuing credentials, and
       the sentence names the thing that limits it. */
    teacherPasswordHint:
      "You will know this password. Ask them to change it on their own Settings screen once they have signed in.",
    passwordTooShort: (n: number) => `At least ${String(n)} characters.`,
    add: "Add teacher",
    adding: "Adding…",
    emailTaken:
      "That email address is already in use. Check whether they already have an account.",
    addFailed: "That teacher was not added.",

    teacherCreated: (name: string) => `${name} can sign in now`,
    teacherCreatedHint: (email: string) =>
      `Give them ${email} and the password you just typed. They can change it themselves afterwards.`,

    setPassword: "Set a password",
    setPasswordFor: (name: string) => `Set a new password for ${name}`,
    /* 🔴 The consequence before the button — G1's rule. Ending every session is
       correct and is not what "change a password" sounds like. */
    setPasswordMeaning:
      "They will be signed out on every device, and will need this password to sign back in.",
    setPasswordSave: "Save password",
    passwordSet: "Password changed. Give it to them and they can sign in.",
    passwordFailed: "That password was not changed.",

    deactivate: "Deactivate",
    deactivating: "Deactivating…",
    /* 🔴 US-3.2: deactivation removes the login and keeps the history. An admin
       who believes it unassigns their classes will not use it. */
    deactivateMeaning:
      "They can no longer sign in. Their classes and every session they ran are kept exactly as they are.",
    deactivateConfirm: "Deactivate this teacher?",
    confirm: "Yes, deactivate",
    cancel: "Cancel",
    deactivateFailed: "That did not change.",

    /*
     * ── E7 · putting one child on a register ──
     *
     * 🔴 THE SCREEN THAT LISTS EVERY CHILD HAD NO WAY TO ADD ONE. The only
     * routes into a school's roster were `POST /roster/import` — a spreadsheet,
     * ADMIN-only against threat T8 — and the teacher tier's own form. So a head
     * teacher enrolling one child mid-term had to re-export the school or ask a
     * class teacher to type a name.
     */
    addStudent: "Add student",
    addStudentClose: "Close form",
    addStudentTitle: "Add a child to a register",
    addStudentHint:
      "The child is on that class's register straight away. The age band comes from the class.",
    studentClass: "Class",
    studentClassChoose: "Choose a class",
    studentName: "Child's name",
    studentRoll: "Roll number",
    studentRollHint: "Optional. A child who arrives before the office assigns one is still on the register.",
    /* 🔴 The two parent fields go together — a name with no number links nobody. */
    studentParentName: "Parent's name",
    studentParentPhone: "Parent's phone",
    studentParentHint:
      "Optional, and the number is what counts: a name on its own links nobody. A number that already belongs to a family is linked rather than duplicated, so one account can cover two children.",
    parentNameNeedsPhone: "Add the family's number as well, or clear the name.",
    /* ⚠️ Named, because "that roll number is taken" sends an admin hunting. */
    rollTakenBy: (name: string, roll: string) =>
      `Roll ${roll} is already ${name}'s in this class. Give this child a different number.`,
    /*
     * 🔴 A WARNING, NOT A REFUSAL, AND THE WORDING HAS TO CARRY THE DIFFERENCE.
     * The server would accept this — it normalises the number it receives and
     * compares it against what is stored, so a typed "1" lands beside a stored
     * "01". Two children a register cannot tell apart is worth a sentence; it is
     * not worth blocking an admin who knows better than we do.
     */
    rollLooksLike: (name: string, roll: string, theirs: string) =>
      `${name} in this class is roll ${theirs}, which a register may read as the same as ${roll}. You can still add this child.`,
    addStudentAction: "Add the child",
    addStudentSaving: "Adding…",
    /*
     * 🔴 WHAT ACTUALLY HAPPENED TO THE FAMILY, NOT JUST "SAVED". Creating a
     * parent account and linking an existing one are different outcomes with
     * different next steps, and the server tells them apart.
     */
    studentAdded: (name: string, className: string) => `${name} is on ${className}'s register.`,
    studentAddedParentCreated:
      "A parent account was created for that number. They can sign in with it to follow their child.",
    studentAddedParentLinked:
      "That number already had a parent account, so this child was added to it.",
    studentAddedNoParent:
      "No family number yet, so nobody can be sent this child's reports. You can add one from the child's own page.",
    addAnotherStudent: "Add another",
    /* ⚠️ One status, three causes — the server sends a code so this can say which. */
    addStudentFailed: "That child was not added.",
    addStudentSeatsFull:
      "This school has no places left on its plan. Nothing was added — ask your Kalqy contact to raise the limit.",
    addStudentPhoneIsStaff:
      "That number belongs to a staff account. Use the family's own number.",
    addStudentBadRequest:
      "Check the name and the phone number. A number must be a 10-digit Indian mobile.",

    /* ── E4 · class assignment ── */
    assignTitle: "Who teaches each class",
    /* 🔴 The consequence, not the instruction. A class with no teacher is
       invisible to every teacher-scoped read in the product. */
    assignHint:
      "A class with nobody assigned cannot be opened by any teacher — no roster, no sessions, no reports.",
    unassigned: (n: number) =>
      n === 1
        ? "1 class has nobody teaching it"
        : `${n} classes have nobody teaching them`,
    unassignedNone: "Every class has a teacher.",
    assignedTo: "Teacher",
    nobody: "Nobody",
    assignFailed: "That class was not reassigned.",

    /*
     * ── E4 · subject teachers, on E3 ──
     *
     * 🔴 THE WORD "TEACHER" NOW MEANS TWO THINGS ON THIS SCREEN, AND THE COPY
     * HAS TO KEEP THEM APART. The class teacher is who is RESPONSIBLE for a
     * class; a subject teacher takes one subject in it. An admin who reads the
     * second as the first will look for the wrong person when a parent rings.
     */
    subjectColumn: "Subject",
    subjectsNone: "None",
    subjectTeachersColumn: "Subject teachers",
    subjectTeacherIn: (subject: string, className: string) => `${subject} in ${className}`,
    /* 🔴 The consequence, which is the entire reason this control exists: an
       assignment is what lets somebody open a class at all. */
    subjectTeacherHint:
      "A teacher assigned to a subject here can open that class, take its register and start a child playing.",
    addSubjectTeacher: "Add a subject teacher",
    subjectTeacherAdded: (name: string, subject: string, className: string) =>
      `${name} now takes ${subject} in ${className}, and can open that class.`,
    subjectTeacherFailed: "That subject teacher was not added.",
    subjectTeacherNoneYet: "Nobody besides the class teacher.",

    /* ── E3 · the subject a new teacher starts with ── */
    teacherSubject: "Subject",
    teacherSubjectPlaceholder: "Maths",
    teacherClass: "Class they teach it in",
    /* ⚠️ NOT "Nobody", WHICH IS THE CLASS TABLE'S WORD FOR AN UNTAUGHT CLASS.
       Here the empty option is about the teacher, not the class, and reusing the
       other sentence would read as "this class has no teacher". */
    noClassYet: "No class yet",
    chooseTeacher: "Choose a teacher",
    teacherSubjectHint:
      "Optional. Name the subject and the class, and the new teacher can open that class the moment they sign in. Leave both empty to assign them later.",
    teacherSubjectClassMissing: "Choose the class as well, or clear the subject.",
    teacherCreatedNoSubject:
      "The account works. They teach nothing yet, so there is no class for them to open until you assign one below.",
    teacherCreatedWithSubject: (subject: string, className: string) =>
      `They take ${subject} in ${className}, so that class is theirs to open.`,
    /* ⚠️ The account exists either way, and the sentence says so first. An
       admin told only that something failed will create the teacher twice. */
    subjectStepFailed:
      "The account was created, but the subject was not assigned. Add it from the table below.",

    /*
     * ── the rebuilt screens' own words ──
     *
     * ⚠️ EVERY STRING BELOW IS NEW SURFACE, NOT NEW BEHAVIOUR. The screens were
     * rebuilt on `AdminUI`, which gave each of them room for the sentence the
     * flat version had nowhere to put — an empty state that says what to do, a
     * help banner at the foot, a search box that has to name what it searches.
     * They are here rather than inline for the reason the rest of this file
     * exists: a sentence hard-coded into a component is a sentence no tier can
     * correct without editing the component.
     */
    searchStaff: "Search by name or email",
    noStaffMatch: "Nobody matches that search.",
    clearSearch: "Clear the search",
    waitingToJoin: "Waiting to join",
    classesWithTeacher: "Classes with a teacher",
    /** 🔴 A count with its denominator, like every other sentence on this tier. */
    assignedFraction: (f: Fraction) =>
      `${f.count} of ${f.total} classes have a teacher`,
    teachersHelpTitle: "How adding a teacher works",
    teachersHelpBody:
      "You create the account and choose the password, then hand both over. They can sign in straight away, and change the password themselves from their own Settings screen.",
    addAnother: "Add another teacher",
    hideForm: "Close",
    staffEmptyDetail:
      "Add the first teacher and give them the login you choose here.",
    assignEmptyTitle: "No classes yet",
    assignEmptyDetail:
      "Classes arrive with the roster import. Once they exist, choose who teaches each one here.",

    dashboardHelpTitle: "What this page is counting",
    dashboardHelpBody:
      "Everything here is this week, and every number carries the total it came out of. There are no percentages on purpose — a share of a class means nothing without the size of the class beside it.",
    classesEmptyTitle: "No classes yet",
    classesEmptyDetail: "Import a roster to create your first class.",

    reportsHelpTitle: "What coverage means",
    reportsHelpBody:
      "A competency code counts as covered once one child has played a game that carries it. It is a record of what the school has touched, never a mark for any child.",
    codesCoveredTitle: "Codes with activity behind them",
    codesUncoveredTitle: "Codes with nothing yet",
    reportsEmptyTitle: "Nothing has been played yet",
    reportsEmptyDetail:
      "Coverage appears as soon as the first child finishes a game.",

    saving: "Saving…",

    rolloverHelpTitle: "Before you run this",
    rolloverHelpBody:
      "Check the plan first: it names every child who would move and every problem that would stop it. Nothing changes until you run it, and a rollover moves the whole school or none of it.",
    stepChoose: "Choose where each class goes",
    stepCheck: "Check the plan",
    stepRun: "Run it once",
    historyEmpty: "No year has been rolled over yet.",
    rolloverNoClasses: "There are no classes in the year that is ending.",

    planHelpTitle: "Who can change this",
    planHelpBody:
      "Only we can change a school’s plan or its seat count. Ask us and we will move it — this page exists so the school can always read what it is on.",

    /* ── E4 · classes, subjects and who takes each one ── */
    classesTitle: "Classes",
    classesIntro: "Each class, who teaches it, and what it studies.",
    classesEmpty: "No classes yet. Add the first one above.",

    /* 🔴 The list is named once for the whole school — a subject typed per class
       is "Maths" beside "maths" within a week, and every question that groups by
       subject then has two answers. */
    subjectsTitle: "Subjects this school teaches",
    subjectsHint:
      "Name each subject once here, then put it on the classes that study it. Naming it twice is how one subject becomes two.",
    subjectName: "Subject",
    subjectPlaceholder: "Maths",
    subjectAdd: "Add",
    subjectsEmpty:
      "No subjects yet. Add the first one to start putting them on classes.",
    subjectUsedBy: (n: number) =>
      n === 0
        ? "on no classes"
        : n === 1
          ? "on 1 class"
          : `on ${String(n)} classes`,
    subjectRemoveLabel: (name: string) => `Remove ${name} from this school`,
    subjectRemoveFromClassLabel: (name: string) =>
      `Remove ${name} from this class`,
    subjectTeacherLabel: (name: string) => `Who takes ${name}`,
    subjectAddFailed: "That subject was not added.",
    subjectRemoveFailed: "That subject was not removed.",
    remove: "Remove",

    addClass: "Add a class",
    addClassHint:
      "You can leave the teacher until later — the class list will remind you.",
    classNamePlaceholder: "Butterflies",
    /* 🔴 "Class 2, Section B" is how an Indian school names a class, and both
       halves are separate columns so a school can be shown all of Class 2. */
    grade: "Grade",
    gradePlaceholder: "LKG",
    section: "Section",
    sectionPlaceholder: "A",
    classTeacher: "Class teacher",
    classTeacherHint:
      "The person responsible for this class. They take any subject that has no teacher of its own.",
    noTeacherYet: "No teacher yet",
    /**
     * 🔴 LABELLED, NEVER BARE. `3RVP9X4J` on its own is a string nobody can act
     * on; an admin on the phone to a parent needs to know it is the thing the
     * child types, and the label is the whole difference.
     */
    classCodeLabel: "Sign-in code",
    /**
     * ⚠️ "NOT ISSUED YET", NOT A BLANK. A class with no code is one no child can
     * sign into, and an empty cell reads as a display fault rather than as
     * something somebody has to do.
     */
    classCodeNone: "No sign-in code yet",
    bandHint:
      "The band decides which games this class is offered. A teacher can still change it for one child.",
    classAddFailed: "That class was not added.",

    childrenCount: (n: number) =>
      n === 1 ? "1 child" : `${String(n)} children`,
    setUp: "Set up",
    close: "Close",

    /* 🔴 Gaps, not errors. A class with no teacher is one somebody has not
       finished setting up, and an admin doing twelve in September needs to see
       which of the twelve to go back to. */
    gapNoTeacher: "Nobody is down as the class teacher.",
    gapNoSubjects: "This class has no subjects yet.",
    gapSubjectsWithoutTeacher:
      "Some subjects have no teacher, and there is no class teacher to take them.",

    archivedReadOnly:
      "This class is archived. Its children have left, so it is kept as history and cannot be edited.",

    classSubjects: "Subjects",
    classSubjectsHint:
      "Leave the teacher as the class teacher unless somebody else takes it.",

    /*
     * 🔴 THE ONE-CHILD ADD THE OFFICE NEVER HAD. The CSV import is the only path
     * that has ever created a child, so a school that gained one in October had
     * to build a one-row spreadsheet — and the roster import screen is not even
     * rendered in this tier. The teacher's version of this form has no parent
     * fields; this one does, which is the whole difference between them.
     */
    addChildTitle: "Add a child to this class",
    addChildHint:
      "For one child who joined after the roster was imported. The parent’s number creates their account if it is new, or links the one they already have.",
    classSubjectsEmpty: "Nothing yet. Add a subject below.",
    addSubject: "Subject",
    chooseSubject: "Choose a subject",
    takenBy: "Taken by",
    /* ⚠️ NOT "None". Empty means the class teacher takes it, which is the
       ordinary arrangement — labelling it "None" would make an admin believe
       they had left a lesson uncovered. */
    theClassTeacher: "The class teacher",
    takenByClassTeacher: (name: string) => `${name} · class teacher`,
    nobodyTakesIt: "Nobody takes this yet",
    noSubjectsNamedYet: "Name a subject at the top of this screen first.",
    everySubjectAlreadyOn:
      "This class already studies every subject the school has named.",

    archiveClass: "Archive this class",
    archiveConfirm: (name: string) => `Archive ${name}?`,
    /* 🔴 What it actually does, before the click. Archiving is how a cohort
       leaves; a hard delete would strand every child in it. */
    archiveMeaning: (n: number) =>
      n === 0
        ? "It stops appearing in the live lists and is kept as history. Nothing is deleted."
        : `Its ${String(n)} children keep their history, their roll number and their parent. The class stops appearing in the live lists and is kept as history. Nothing is deleted.`,
    archiveConfirmYes: "Yes, archive it",
    archiving: "Archiving…",
    archiveFailed: "That class was not archived.",

    /*
     * ── what the school is actually playing ─────────────────────────────────
     *
     * 🔴 IT IS NOT COVERAGE, AND THE HINT DRAWS THE LINE IN ONE SENTENCE. A
     * school can have full NCF coverage and two classes that have never opened a
     * game — coverage counts one event, and this counts use.
     */
    gamesUsageTitle: "What the school is playing",
    gamesUsageHint:
      "Across every class. A game with nothing against it has not been started anywhere — which is a different thing from a game going badly.",

    /*
     * ── E7 · every child in the school ──────────────────────────────────────
     *
     * 🔴 "CHILDREN", NEVER "STUDENTS' RECORDS" OR "PROFILES". A school reads the
     * second as a file it is legally responsible for and asks what is in it; the
     * honest answer is a name, a roll number, an age band and what they played
     * (ADR-004), and the screen says so rather than letting the word imply more.
     */
    studentsTitle: "Every child in the school",
    studentsSubtitle:
      "Across every class, in register order. Open a child to read their report.",
    studentsSearch: "Search by name, roll, class or parent",
    studentsNoMatch: "Nobody matches that search.",
    studentsClear: "Clear the filters",
    studentsShowingOf: (shown: number, total: number) =>
      `Showing ${String(shown)} of ${String(total)} children`,

    /* ⚠️ A VIEW CONTROL, NOT A HEADLINE. See `studentsModel.ts`: a school-wide
       engagement figure computed here would be a second answer to E1's tile. */
    filterEveryone: "Everyone",
    filterRecent: "Played in the last 7 days",
    filterQuiet: "Quiet for a week or more",
    filterNever: "Never played",
    everyClass: "Every class",

    tileChildren: "Children",
    tileClasses: "Classes",
    tileNeverPlayed: "Never played",
    tileNoParent: "No parent contact",
    /** 🔴 A count an admin can act on this morning, with what to do about it. */
    neverPlayedHint:
      "These children have no recorded activity at all. Their class may not have started yet.",
    noParentHint:
      "These children have no parent name and no phone, so no report can reach a family.",

    exportStudents: "Export the list",
    /** ⚠️ Named, so nobody is surprised by what opens in the spreadsheet. */
    exportStudentsHint:
      "Class, roll, name, band, parent and last played. Phone numbers are exported masked, exactly as they are shown.",

    childName: "Child",
    roll: "Roll",
    parent: "Parent",
    lastPlayed: "Last played",
    openReport: "Report",
    neverPlayedShort: "Never",
    playedRecently: "This week",
    playedEarlier: "Not this week",
    noRoll: "No roll number",

    /*
     * ── E8 · one child's report ─────────────────────────────────────────────
     *
     * 🔴 IT IS A READING SURFACE, AND THE SENTENCE IS ON THE SCREEN. S6 makes a
     * principal school read-only, and the roll number, the class and the age
     * band are already edited on E4 — a second place to change them is a second
     * answer to "where does this get fixed". D4: no control at all, rather than
     * one that duplicates or one that cannot work.
     */
    reportBack: "All children",
    reportReadOnly:
      "Reading only. Roll numbers, classes and age bands are changed on the Classes screen.",
    reportFacts: "What the product holds about this child",
    /** 🔴 ADR-004 — the ceiling is deliberate, so the screen states it. */
    noOtherData:
      "A name, a roll number, an age band and what they played. There is no date of birth, address, photograph or note, by design.",
    parentPhoneLabel: "Parent phone",
    phoneMasked: "Shown masked. The full number never leaves the server.",
    noParent: "No parent linked",
    noParentWhy:
      "Nothing can be sent home for this child until a parent name and phone are on the roster.",

    progressTitle: "Progress so far",
    coins: "Coins",
    stickers: "Stickers",
    streak: "Day streak",
    skillsTitle: "Skills",
    contentSkills: "What they are learning",
    motorSkills: "How they are moving",
    /* 🔴 A LEVEL, NEVER A PERCENTAGE OF A CURRICULUM (§8, ADR-037). */
    level: (n: number) => `Level ${String(n)} of 3`,
    notStartedSkill: "Not started",
    activities: (n: number) => `${String(n)} activities`,
    motorHint:
      "Movement skills are earned across all five games, so they are counted rather than levelled.",

    strengthsTitle: "Strong and stuck",
    strengthsHint:
      "Measured from this child's own answers, against their classmates. Every row shows the count it was made from.",
    strengthsPeers: (n: number) =>
      n === 0
        ? "No classmates have answered enough for a comparison yet."
        : `Compared with ${String(n)} classmates.`,
    evidence: (correct: number, attempts: number) =>
      `${String(correct)} right of ${String(attempts)} answered`,
    classShare: (percent: number) => `Class ${String(percent)}%`,
    /** 🔴 `play.prisma` — the only signal that a level's content is too hard. */
    ceiling: (n: number) =>
      `Moved up from ${String(n)} ${n === 1 ? "level" : "levels"} without mastering it`,
    topLevel: (n: number) => `Reached level ${String(n)}`,

    childGamesTitle: "What this child has played",
    childGamesHint:
      "Their own rounds and levels. A game with nothing against it has not been started.",

    sessionsTitle: "Sittings",
    sessionsHint:
      "When this child sat down to play. A sitting that recorded nothing wrote no activity at all, which is not the same as playing badly.",
    sessionOpen: "Still open",
    sessionNotRecorded: "Not recorded",

    activityTitle: "Recent activity",
    activityWhen: "When",
    activityGame: "Game",
    activityWhat: "What happened",
    activitySkill: "Skill",
    activityLevel: "Level",
    /** ⚠️ The server caps this read at 50 (`PlayersReadService.recentEvents`),
        and a table that silently stops at fifty rows reads as missing data. */
    activityCap: "The fifty most recent, newest first.",
    activityShowAll: (n: number) => `Show all ${String(n)}`,
    activityShowFewer: "Show fewer",
    neverPlayedYet: "This child has not played yet.",

    /*
     * ── E8 · the two things this screen DOES ────────────────────────────────
     *
     * 🔴 THE MENU HELD TWO `alert('… triggered')` STUBS. "Send Message" and
     * "Export Profile" opened a browser alert and did nothing else, on a page
     * that names a real child — the dead-control defect `DashboardShell` records
     * twice over: *"a control that looks like the way out and is not is worse
     * than no control: the reader stops looking for the real one."*
     *
     * ⚠️ NEITHER OF THEM EDITS THE CHILD'S RECORD, so the screen's own rule
     * stands unchanged. One writes a message to the family — correspondence, not
     * a record — and the other prints what is already on screen. Roll numbers,
     * classes and age bands are still only changed on E4.
     */
    actionsMenu: "Actions",

    /*
     * 🔴 IT IS THE SAME THREAD C4 AND D6 USE, NOT A NEW CHANNEL. `POST
     * /players/:id/messages` admits TEACHER and ADMIN and writes both as the
     * TEACHER side — *"an ADMIN writes as the school because an admin IS the
     * school's office"* (`messages.service.ts`). So the family reads it in the
     * app, in the conversation about this child, beside what their teacher
     * wrote. The copy has to say that, or an admin will assume it is an SMS and
     * write something time-critical into a channel nobody is watching.
     */
    messageAction: "Send message",
    messageDialog: "Message this child’s family",
    messageTo: (who: string) => `To ${who}`,
    messageHint:
      "It appears in the family’s Kalqy app, in the conversation about this child, under your name. It is not an SMS and no phone number is used.",
    messageWrite: "Your message",
    messagePlaceholder: "Type your message…",
    messageSend: "Send",
    messageSending: "Sending…",
    messageSent: (who: string) => `Sent to ${who}.`,
    messageSentNobody: "Saved to the thread.",
    /** ⚠️ Both numbers, like every other count in this file. */
    messageCount: (n: number, max: number) =>
      `${String(n)} of ${String(max)} characters`,
    messageClose: "Close",
    messageCancel: "Cancel",
    /*
     * ⚠️ A SEND FAILURE IS NOT A LOAD FAILURE, AND "Couldn’t load this" ON A
     * COMPOSER IS THE WRONG SENTENCE — it reads as though the dialog failed to
     * open, not as though the message did not go. It also has to say the message
     * was NOT sent, because the one thing the writer cannot see is whether it
     * half-worked.
     */
    messageFailed: "Couldn’t send that. Nothing was sent — try again.",
    messageRefused:
      "This account cannot message families. A school admin can, a principal cannot.",
    messageRejected:
      "The message was empty or too long, so nothing was sent.",

    /*
     * 🔴 THE EXPORT IS `window.print()` AND A DOCUMENT WRITTEN FOR PAPER, which
     * is the same answer C2 reached and the same one OD-59 forced: a
     * server-side renderer would put headless Chromium on the node and a
     * rendering dependency on a route that emits children's names. What was
     * missing there is what was missing here — something to print. The shell is
     * `h-dvh overflow-hidden` with the page inside a scroller, so the browser
     * would otherwise be handed one viewport and produce a single sheet with the
     * rest of the child clipped off the bottom.
     */
    exportAction: "Export profile",
    exportHint: "Opens the print dialogue — choose “Save as PDF” to keep a copy.",
    sheetTitle: "Child profile",
    sheetPrepared: (when: string) => `Prepared ${when}`,
    /*
     * 🔴 THE SHEET CARRIES ONLY WHAT THE PRODUCT MEASURED, AND SAYS SO. The
     * screen's Overview tab still draws placeholder subject percentages, an
     * invented attendance ring and a hardcoded joining date; none of it is on
     * this document. A printed page about a named child, on a school's desk, is
     * the last place an invented number should be able to reach.
     */
    sheetSource:
      "Every figure here is a count of something this child did, with the number it was counted from beside it. Nothing on this page is an estimate, a grade or a comparison with another child.",
    sheetIdentity: "The child",
    /** ⚠️ SINGULAR. E7's tile counts classes; this names the one they are in. */
    sheetClass: "Class",
    /** ⚠️ "Activity" ALONE OVER A NUMBER SAYS NOTHING — 52 of what. */
    sheetEventsCounted: "Activities recorded",
    sheetProgress: "Progress so far",
    sheetSkills: "Skills",
    sheetStrengths: "Strong and stuck",
    sheetSessions: "Recent sittings",
    sheetActivity: "Recent activity",
    sheetNothingYet: "Nothing recorded yet.",
    /** ⚠️ A panel that never arrived is not a panel that is empty. */
    sheetNotLoaded: "This section had not loaded when the page was printed.",
    sheetShowing: (shown: number, total: number) =>
      `Showing ${String(shown)} of ${String(total)}, newest first.`,

    /* ── shared ── */
    dash: "—",
    loadFailed: "Couldn’t load this.",
    notAnAdmin:
      "This screen is for school admins. Sign in with an admin account.",
    lastWorked: (at: string) => `Last loaded ${at}`,
    neverWorked: "It hasn’t loaded yet",
    retry: "Try again",

    /*
     * ── E5 · the head teacher's analytics screen ──
     *
     * 🔴 EVERY STRING HERE IS NEW BECAUSE THE SCREEN HAD NONE. `SchoolReports`
     * carried its labels as literals in the markup — "Total Students", "Average
     * Score", "Assignments" — beside the invented figures under them. Copy in a
     * component is copy no tier can correct, which is the argument `GamesPanel`
     * spends its whole header making.
     */
    reportsHint:
      "What this school has actually done — enrolment, what the children are playing, and which NCF-FS codes are evidenced.",

    reportsChildren: "Children enrolled",
    /* 🔴 A child with no class is invisible to every scoped query in the
       product. It should be zero, and naming it is how it gets fixed. */
    reportsNoClass: (n: number) =>
      n === 1 ? "1 child is in no class" : `${String(n)} children are in no class`,
    reportsAllPlaced: "Every child is in a class",
    reportsClasses: "Classes",
    reportsArchived: (n: number) =>
      n === 0 ? "None archived" : `${String(n)} archived`,
    reportsTeachers: "Teachers",
    reportsPlayed: "Children who played",
    reportsSittings: "Sittings",
    /* ⚠️ THE WINDOW TRAVELS WITH THE FIGURE. A count with no period is a
       number — OD-39/OD-72 applied to time. */
    reportsWindow: (days: number) => `in the last ${String(days)} days`,
    reportsEverPlayed: "Have ever played",
    reportsEverPlayedHint: "All time, not this window",

    reportsGamesTitle: "What the school is playing",
    reportsGamesHint:
      "Rounds finished, right answers and the level reached, across every class. A game with nothing against it has not been started — which is a different fact from a game going badly.",

    reportsCoverageTitle: "Curriculum coverage",
    /* 🔴 ONE EVENT IS EVIDENCE. Said out loud, because coverage and engagement
       look alike on a page and mean opposite things. */
    reportsCoverageHint:
      "Which NCF-FS codes this school has evidenced. One recorded event counts as evidence — this is not a measure of how much they play.",
    reportsCoverageEmpty: "Nothing has been played yet, so no code is evidenced.",
    reportsExportCoverage: "Export coverage (CSV)",
    reportsGaps: (n: number) =>
      n === 1 ? "1 code with nothing against it" : `${String(n)} codes with nothing against them`,

    reportsClassesTitle: "Classes that need a look",
    reportsClassesHint: (days: number) =>
      `Children who played against children enrolled, over the last ${String(days)} days. Worst first.`,
    reportsClassesEmpty: "This school has no classes yet.",
    reportsClassCol: "Class",
    reportsTeacherCol: "Teacher",
    /* 🔴 A CLASS WITH NO TEACHER IS THE FINDING, not a blank cell. */
    reportsNoTeacher: "Nobody assigned",
    reportsPlayedCol: "Played",
    reportsSittingsCol: "Sittings",

    reportsSkillsTitle: "Skills across the school",
    reportsSkillsHint:
      "Recorded events per skill, and how many children are behind them. Counts rather than a share — a percentage here would read as a grade.",
    reportsSkillCol: "Skill",
    reportsGame: "Game",
    reportsCategory: "Category",
    reportsCodes: "Codes",
    reportsEvents: "Events",
    reportsChildrenCol: "Children",

    /* ── E5 charts ──────────────────────────────────────────────────────────
     *
     * 🔴 EVERY TITLE NAMES THE MEASURE, NOT THE PICTURE. "Engagement" is a word
     * a school reads as a grade; "Children who have played" is the thing that
     * was counted. The hints carry the window, for the reason `SchoolOverview`
     * ships `windowDays` at all — a figure without its period is not a figure.
     */
    chartReachTitle: "Children who have played",
    chartReachHint: (days: number) =>
      `Out of every child on the roll, over the last ${String(days)} days and over all time.`,
    chartPlayedRow: (days: number) => `Played in the last ${String(days)} days`,
    chartEverRow: "Have ever played",
    chartNoChildren: "No children on the roll yet.",

    chartBandsTitle: "Children by age band",
    chartBandsHint: "Every child in a class, grouped by that class's band.",
    /* ⚠️ Not "children": a child with no class is not in this total, and the
       tile above is the one place they are counted. */
    chartInClasses: "in classes",
    chartNoClasses: "No classes yet.",

    chartClassReachTitle: "Playing, by class",
    chartClassReachHint: (days: number) =>
      `How many of each class's own children played in the last ${String(days)} days. Quietest first.`,
    chartSittingsTitle: "Sittings, by class",
    chartSittingsHint: (days: number) =>
      `Sittings started in the last ${String(days)} days. Scaled to the busiest class.`,

    chartGameReachTitle: "Children reached, by game",
    chartGameReachHint: "How many children have played each game, out of the whole school.",
    chartRoundsTitle: "Rounds finished, by game",
    chartRoundsHint: "Levels completed. Scaled to the most played game.",
    chartAnswersTitle: "Answers, by game",
    chartAnswersHint:
      "Right and wrong answers across the school. This is a measure of the game, never a score for a child.",
    chartRight: "right",
    chartWrong: "wrong",
    chartNoGames: "Nobody has opened a game yet.",

    chartSkillReachTitle: "Children reached, by skill",
    chartSkillReachHint: "How many children have any recorded work against each skill.",
    chartSkillEventsTitle: "Recorded work, by skill",
    chartSkillEventsHint: "Events recorded against each skill. Scaled to the largest.",
    chartNoSkills: "Nothing recorded yet.",

    chartEvidenceTitle: "Evidence, by game",
    chartEvidenceHint:
      "Events recorded against each game's curriculum codes. A game at zero has evidenced nothing yet.",
  },
} as const;

/**
 * 🔴 A 401 OR 403 ON A SCHOOL SCREEN MEANS "NOT AN ADMIN", NOT "OFFLINE".
 *
 * Staff sign-in exists now (§10.0), so unlike the teacher tier these screens can
 * genuinely load — which makes the remaining refusal a *role* problem. A teacher
 * who opens E1 needs to be told that, or they spend the morning hunting a
 * network fault that does not exist. `teacher/copy.ts` and `school/copy.ts`
 * apply the same discipline to the failure they each actually have.
 */
export function describeAdminError(err: Error | null, lang: Lang): string {
  const t = ADMIN_COPY[lang];
  const status = (err as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return t.notAnAdmin;
  return t.loadFailed;
}

/**
 * Why a message did not go.
 *
 * 🔴 A SEPARATE MAPPER FROM `describeAdminError`, BECAUSE A WRITE FAILS
 * DIFFERENTLY FROM A READ. That one answers *"this screen would not load"* and
 * its 403 sentence — "This screen is for school admins" — is wrong here twice
 * over: the screen loaded fine, and the account that gets the 403 is a
 * PRINCIPAL, who IS an admin of the school and is refused for a different reason
 * (`messages.service.ts` §assertAdult: oversight is not membership of somebody
 * else's correspondence).
 *
 * ⚠️ THE STATUS IS ALL THERE IS TO GO ON. `client.ts` deliberately drops the
 * server's own message — *"a 400 echoes validation messages about fields that
 * carry a child's name"* — so this maps the code, and every branch says whether
 * anything was sent.
 */
export function describeMessageError(err: Error | null, lang: Lang): string {
  const t = ADMIN_COPY[lang];
  const status = (err as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return t.messageRefused;
  if (status === 400) return t.messageRejected;
  return t.messageFailed;
}

export function noteText(note: SchoolNote, lang: Lang, count: number): string {
  const t = ADMIN_COPY[lang];
  switch (note) {
    case "NO_CHILDREN":
      return t.noteNoChildren;
    case "NOBODY_PLAYED":
      return t.noteNobodyPlayed;
    case "NO_TEACHERS":
      return t.noteNoTeachers;
    default:
      return t.noteChildrenWithNoClass(count);
  }
}

export const CLASS_SORT_LABEL: Record<ClassSort, Record<Lang, string>> = {
  name: { en: ADMIN_COPY.en.sortName },
  quietest: { en: ADMIN_COPY.en.sortQuietest },
  largest: { en: ADMIN_COPY.en.sortLargest },
};

export const BAND_LABEL: Record<
  "BAND_3_4" | "BAND_4_5" | "BAND_5_6",
  Record<Lang, string>
> = {
  // ⚠️ Always a band, never "age 4" (docs/README.md Conventions). The numerals
  // are the same in both languages because they are the band's name, not a word.
  BAND_3_4: { en: "3–4" },
  BAND_4_5: { en: "4–5" },
  BAND_5_6: { en: "5–6" },
};

/**
 * What a skill is called on a child's report.
 *
 * ⚠️ A SECOND TABLE, NOT AN IMPORT FROM THE TEACHER TIER.
 * `features/dashboards/school` importing from `features/dashboards/teacher` is
 * the sideways dependency `folderstructure.md` forbids, and `copy.ts` records
 * the same trade one screen earlier: eight strings duplicated against a shared
 * module built for one caller. E1 has no such table because it names content
 * skills by their GAME — this report has to name the motor skills too, and
 * "Balance" is not a game.
 */
export const SKILL_LABEL: Record<string, Record<Lang, string>> = {
  NUMERACY: { en: "Numbers" },
  LITERACY: { en: "Letters and words" },
  GENERAL_AWARENESS: { en: "The world around us" },
  SOCIAL_AWARENESS: { en: "Getting on with others" },
  MATCHING: { en: "Matching and sorting" },
  COORDINATION: { en: "Coordination" },
  BALANCE: { en: "Balance" },
  BODY_AWARENESS: { en: "Body awareness" },
};

/**
 * What one recorded event says, in words a head teacher reads.
 *
 * 🔴 THE RAW ENUM IS A DATABASE SLUG AND MUST NOT REACH THE PAGE. E5 already
 * records this mistake in its own comment — it printed `r.game` raw, so
 * *"sky-quest"* was the boldest thing on a document a school might act on — and
 * the event table is the same failure with `ANSWER_CORRECT` in it.
 *
 * ⚠️ A GESTURE IS NAMED BY WHAT THE CHILD DID, NOT BY THE SIGNAL THAT SAW IT. A
 * school reading "GESTURE_SQUAT" learns about the camera; "Ducked down" is the
 * thing that happened in their classroom.
 *
 * ⚠️ AN UNKNOWN VALUE FALLS BACK TO ITSELF AT THE CALL SITE. `EventRow.type` is
 * typed as `string` by the API module, and a value added to the enum later must
 * render as itself rather than as a blank cell.
 */
export const EVENT_LABEL: Record<string, Record<Lang, string>> = {
  SESSION_START: { en: "Started playing" },
  SESSION_END: { en: "Finished playing" },
  ANSWER_CORRECT: { en: "Answered correctly" },
  ANSWER_WRONG: { en: "Answered wrongly" },
  LEVEL_COMPLETE: { en: "Completed a level" },
  /* 🔴 `play.prisma` keeps mastery and ceiling apart because ceiling
     advancement looks identical to success in every average. The word here is
     neutral for that reason — the panel above says which of the two it was. */
  LEVEL_UP: { en: "Moved up a level" },
  GESTURE_JUMP: { en: "Jumped" },
  GESTURE_SQUAT: { en: "Ducked down" },
  GESTURE_REACH: { en: "Reached out" },
  GESTURE_STEP: { en: "Stepped across" },
  GESTURE_ACT: { en: "Acted it out" },
  GESTURE_PLACE: { en: "Placed a piece" },
  REWARD_EARNED: { en: "Earned a reward" },
  /* A pose met or missed, a gate that timed out — neither right nor wrong, so
     the words say neither. */
  ITEM_RESULT: { en: "Took a turn" },
};

/**
 * The five verdicts `GET /players/:id/strengths` can return.
 *
 * 🔴 TWO OF THEM ARE REFUSALS, AND THEY ARE THE MOST IMPORTANT ONES ON A FRESH
 * SCHOOL. "Not started" and "too early to say" keep the panel from opening as a
 * list of gaps in the first week of a pilot — which is how a panel teaches a
 * head teacher to ignore it.
 */
export const STANDING_LABEL: Record<string, Record<Lang, string>> = {
  STRONG: { en: "Strong" },
  STEADY: { en: "Steady" },
  NEEDS_PRACTICE: { en: "Needs practice" },
  NOT_ENOUGH_YET: { en: "Too early to say" },
  NOT_STARTED: { en: "Not started" },
};
